import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Loader2, Inbox } from "lucide-react";
import { API } from "@/admin/AuthContext";
import AdminLayout from "@/admin/AdminLayout";

const POOLS = [
  { key: "politics", label: "Politics (QGs)", color: "#208BFF" },
  { key: "agency", label: "Agências", color: "#7E20FF" },
  { key: "saas", label: "Dedicada", color: "#3ED383" },
  { key: "campaign", label: "Campanhas", color: "#FA9F22" },
  { key: "agent", label: "Agentes", color: "#1B1D1D" },
];

export default function AdminLeads() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState("politics");

  useEffect(() => {
    axios
      .get(`${API}/admin/leads`, { withCredentials: true })
      .then(({ data }) => setData(data))
      .finally(() => setLoading(false));
  }, []);

  const pool = useMemo(() => data?.pools[active] || [], [data, active]);
  const columns = useMemo(
    () => (pool.length ? Object.keys(pool[0]).filter((k) => k !== "id") : []),
    [pool],
  );

  if (loading) {
    return (
      <AdminLayout title="Leads consolidados">
        <div className="flex items-center justify-center py-20 text-[#7D8188]">
          <Loader2 size={20} className="animate-spin" />
        </div>
      </AdminLayout>
    );
  }
  if (!data) return null;

  return (
    <AdminLayout
      title="Leads consolidados"
      subtitle="Todos os pools de leads gerados pelas verticais do site."
    >
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-6">
        {POOLS.map((p) => (
          <button
            key={p.key}
            data-testid={`leads-tab-${p.key}`}
            onClick={() => setActive(p.key)}
            className={`text-left rounded-2xl border p-4 transition-all duration-200 ${
              active === p.key
                ? "border-[#1B1D1D] bg-white shadow-sm"
                : "border-[#1B1D1D]/8 bg-white hover:border-[#1B1D1D]/20"
            }`}
          >
            <div
              className="text-[10px] font-bold tracking-[0.2em] uppercase"
              style={{ color: p.color }}
            >
              {p.label}
            </div>
            <div className="mt-1 font-display text-2xl font-bold text-[#1B1D1D]">
              {data.totals[p.key] ?? 0}
            </div>
          </button>
        ))}
      </div>

      {pool.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-[#1B1D1D]/15 bg-white p-12 text-center text-[#7D8188]">
          <Inbox size={20} className="inline-block mb-2" />
          <p className="text-sm">Nenhum lead ainda neste pool.</p>
        </div>
      ) : (
        <div className="rounded-2xl border border-[#1B1D1D]/8 bg-white overflow-x-auto">
          <table className="w-full text-sm min-w-[720px]">
            <thead className="bg-[#F5FAFB] text-[10px] uppercase tracking-[0.18em] text-[#7D8188]">
              <tr>
                {columns.map((k) => (
                  <th key={k} className="text-left px-4 py-3 font-semibold">
                    {k.replace(/_/g, " ")}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-[#1B1D1D]/8">
              {pool.map((row, i) => (
                <tr key={row.id} data-testid={`leads-row-${active}-${i}`}>
                  {columns.map((k) => (
                    <td key={k} className="px-4 py-3 align-top text-[#1B1D1D]">
                      {renderCell(k, row[k])}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </AdminLayout>
  );
}

function renderCell(key, v) {
  if (v === null || v === undefined) return <span className="text-[#7D8188]">—</span>;
  if (key === "created_at") {
    try {
      return new Date(v).toLocaleString("pt-BR", {
        dateStyle: "short",
        timeStyle: "short",
      });
    } catch {
      return String(v);
    }
  }
  if (Array.isArray(v)) {
    return (
      <div className="flex flex-wrap gap-1">
        {v.map((x, i) => (
          <span
            key={String(x)}
            className="rounded-full bg-[#F5FAFB] px-2 py-0.5 text-[10px] border border-[#1B1D1D]/8"
          >
            {String(x)}
          </span>
        ))}
      </div>
    );
  }
  if (typeof v === "boolean") return v ? "sim" : "não";
  const s = String(v);
  return s.length > 80 ? s.slice(0, 80) + "…" : s;
}
